import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { CtaBand } from "@/components/CtaBand";
import { SectionHeader } from "@/components/SectionHeader";
import { PillButton } from "@/components/PillButton";
import { Icon } from "@/lib/icons";
import { IMG } from "@/lib/images";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { CardsSkeleton, ErrorState, EmptyState } from "@/components/States";
import { useServices } from "@/hooks/useSiteData";

export default function ServicesPage() {
  const { data, isLoading, isError, refetch } = useServices();
  return (
    <>
      <Seo title="Services" description="Consulting, customer experience, UX design, commerce platforms, AR/VR, mobile, cloud transformation and enterprise automation services from Jonaren Technology." path="/services" />
      <PageHero eyebrow="Our services" title="End-to-end capabilities for enterprise digital platforms." lead="Eight practices that work together - so strategy, design, engineering and operations are delivered by one accountable team." crumbs={[{ label: "Services" }]} image={IMG.collaboration} testId="services-page-hero" />

      <section className="section paper">
        <div className="container-x">
          <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-6"><SectionHeader eyebrow="Explore our services" title="What we are offering to our potential clients" /></div>
            <Reveal className="lg:col-span-6"><p className="max-w-md text-muted-foreground lg:ml-auto">We believe in delivering top-tier technology solutions without compromising your budget. Every engagement starts with a clear scope and a measurable outcome.</p></Reveal>
          </div>
          {isLoading && <CardsSkeleton className="mt-12" count={6} />}
          {isError && <div className="mt-12"><ErrorState onRetry={refetch} /></div>}
          {data && data.length === 0 && <div className="mt-12"><EmptyState title="Services are being updated" text="Please check back shortly or contact us directly." /></div>}
          {data && data.length > 0 && (
            <Stagger className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {data.map((s, i) => (
                <StaggerItem key={s.id}>
                  <Link to={`/services/${s.slug}`} className="group flex h-full flex-col rounded-3xl border border-border bg-white p-6 card-hover" data-testid={`services-list-card-${s.slug}`}>
                    <div className="flex items-start justify-between"><span className="font-mono text-xs text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>{s.featured && <span className="rounded-full bg-primary px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white">Featured</span>}</div>
                    <div className="mt-5 flex gap-5">
                      <h2 className="vertical-text shrink-0 font-heading text-xl font-medium leading-tight">{s.title}</h2>
                      <div className="photo aspect-[4/5] flex-1 rounded-2xl"><img src={s.image} alt={s.title} loading="lazy" /></div>
                    </div>
                    <p className="mt-6 text-sm text-muted-foreground">{s.tagline}</p>
                    <ul className="mt-4 space-y-1.5">{s.capabilities.slice(0, 3).map((c) => <li key={c} className="flex items-start gap-2 text-xs font-medium"><CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />{c}</li>)}</ul>
                    <div className="mt-auto flex items-center justify-between pt-6">
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#f6f6f4] text-foreground transition-colors group-hover:bg-primary group-hover:text-white"><Icon name={s.icon} className="h-4 w-4" /></span>
                      <span className="link-arrow">Read more <ArrowUpRight /></span>
                    </div>
                  </Link>
                </StaggerItem>
              ))}
            </Stagger>
          )}
        </div>
      </section>

      <section className="bg-white py-16 sm:py-20">
        <div className="container-x grid items-center gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-6"><div className="photo aspect-[4/3] rounded-3xl"><img src={IMG.presentation} alt="Workshop" loading="lazy" /></div></Reveal>
          <Reveal delay={0.1} className="lg:col-span-6">
            <span className="eyebrow">How we engage</span>
            <h2 className="section-title mt-4">Not sure where to start? Begin with a discovery sprint.</h2>
            <p className="mt-5 text-muted-foreground">A 2-4 week discovery sprint gives you a clear scope, architecture and costed roadmap before you commit to a build. Most clients start here.</p>
            <div className="mt-8"><PillButton to="/contact?interest=Consulting%20%26%20Strategy" variant="dark" testId="services-book-discovery-button">Book a discovery call</PillButton></div>
          </Reveal>
        </div>
      </section>
      <div className="paper pt-16 sm:pt-20"><CtaBand /></div>
    </>
  );
}
